import { formatDistanceToNow } from "date-fns";
import { enUS, nb, pl, sv } from "date-fns/locale";

export const FILE_TYPES = {
  doc: { color: "#2563EB", tint: "#EAF0FE", ext: "DOC" },
  pdf: { color: "#DC2626", tint: "#FDECEC", ext: "PDF" },
  sheet: { color: "#16A34A", tint: "#E9F7EE", ext: "XLS" },
  deck: { color: "#D97706", tint: "#FDF2E3", ext: "PPT" },
  img: { color: "#7C3AED", tint: "#F1ECFD", ext: "IMG" },
  file: { color: "#4A5A57", tint: "#F0F3F2", ext: "FIL" },
};

export const FOLDER_TINTS = [
  { color: "#0F766E", tint: "#E6F2F0" },
  { color: "#2563EB", tint: "#EAF0FE" },
  { color: "#D97706", tint: "#FDF2E3" },
  { color: "#7C3AED", tint: "#F1ECFD" },
];

export const ARCHIVE_ACCEPT =
  "image/png,image/jpeg,image/jpg,image/gif,image/svg,image/svg+xml,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv";

export function fileExtension(filename = "") {
  const part = String(filename).split(".").pop() || "";
  return part.slice(0, 4).toUpperCase() || "FIL";
}

export function fileTypeStyle(document) {
  const name = (
    document?.file?.filename ||
    document?.title ||
    ""
  ).toLowerCase();
  const ext = fileExtension(name).toLowerCase();
  if (ext === "pdf") return { ...FILE_TYPES.pdf };
  if (["xls", "xlsx", "csv"].includes(ext)) return { ...FILE_TYPES.sheet };
  if (["doc", "docx"].includes(ext)) return { ...FILE_TYPES.doc };
  if (["ppt", "pptx", "key"].includes(ext)) return { ...FILE_TYPES.deck };
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
    return { ...FILE_TYPES.img, ext: ext.toUpperCase().slice(0, 4) };
  }
  const typed = FILE_TYPES.file;
  return { ...typed, ext: fileExtension(name) };
}

export function formatFileSize(sizeKb) {
  const kb = Number(sizeKb);
  if (!kb || Number.isNaN(kb)) return "—";
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const PREFS_KEY = "promang.document_archive.prefs";

export function readArchivePrefs() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PREFS_KEY) || "{}");
    return {
      view: parsed.view === "list" ? "list" : "grid",
    };
  } catch {
    return { view: "grid" };
  }
}

export function writeArchivePrefs(patch) {
  const next = { ...readArchivePrefs(), ...patch };
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export async function downloadDocumentFile(document) {
  const url = document?.file?.url;
  if (!url) throw new Error("missing");
  const filename =
    document?.file?.filename || document?.title || "dokument";
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("fetch");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const link = window.document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    link.target = "_blank";
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

export function dateLocale(lang) {
  if (lang?.startsWith("sv") || lang?.startsWith("se")) return sv;
  if (lang?.startsWith("pl")) return pl;
  if (lang?.startsWith("en")) return enUS;
  return nb;
}

export function relativeTime(value, lang) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: dateLocale(lang),
  });
}

export function documentDisplayName(document) {
  return document?.title || document?.file?.filename || "Uten tittel";
}

export function isDocumentInFolder(document, folderId) {
  if (!folderId) {
    return document?.module === "project";
  }
  return (
    document?.module === "folder" &&
    String(document?.module_param) === String(folderId)
  );
}

export function countFilesInFolder(documents, folderId) {
  return (documents || []).filter((doc) => isDocumentInFolder(doc, folderId))
    .length;
}

export function countFilesInFolders(documents, folderIds) {
  const set = new Set((folderIds || []).map(String));
  return (documents || []).filter(
    (doc) => doc?.module === "folder" && set.has(String(doc.module_param))
  ).length;
}

export function projectArchivePath(projectId, folderId) {
  const base = `/dashboard/projects/${projectId}/archive`;
  if (!folderId) return base;
  return `${base}?folder=${encodeURIComponent(String(folderId))}`;
}
